import styled from 'styled-components';
import { useContext, useMemo } from 'react';

import Row from '../../Common/Row';
import Typography from '../../../kingpin/atoms/Typography';
import useGetFieldLabel from '../../../hooks/useGetFieldLabel';
import useValueFormatters from '../../../hooks/useValueFormatters';
import { BonusOverviewContext } from '../providers/BonusOverviewProvider';
import formatRewardRate from '../utils/formatRewardRate';
import getRewardUnits from '../utils/getRewardUnits';
import { isArnoldBrosRewardBands } from '../utils/rewardBandsUtils';

const Outer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 300px;
  background-color: var(--color-tooltip-bg);
  border-radius: 8px;
`;

const Section = styled.div`
  background-color: var(--color-tooltip-section-bg);
  padding: 16px;
  border-radius: 8px;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TableTitleDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--color-tooltip-divider);
`;

const BandRow = styled.div<{ isActive?: boolean }>`
  padding: 4px 8px;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
  background-color: ${({ isActive }) =>
    isActive ? 'var(--color-tooltip-row-active-bg)' : 'transparent'};
`;

const getBandThreshold = (
  band: BonusTypes.Rewards.RewardBand,
): { operator: string; value: number } | undefined => {
  if (band.min != null) {
    return { operator: '>=', value: band.min };
  }
  if (band.max != null) {
    return { operator: '<=', value: band.max };
  }
  if (band.equal != null) {
    return { operator: '=', value: band.equal };
  }
  return undefined;
};

const getFinalPay = ({
  reward,
  activeBand,
}: {
  reward: BonusTypes.Rewards.AnyRewardOutput;
  activeBand?: BonusTypes.Rewards.RewardBand;
}): number | undefined => {
  if (reward.reward !== undefined) {
    return reward.reward;
  }

  if (reward.type === 'fixedBanded') {
    return activeBand?.value;
  }

  if (reward.type === 'multiplierBanded') {
    return activeBand?.reward ?? undefined;
  }

  return undefined;
};

const getRateUnits = ({
  units,
  multiplierField,
}: {
  units?: string;
  multiplierField?: string;
}): string | undefined => {
  if (units) {
    return units;
  }

  if (multiplierField?.toLowerCase().includes('miles')) {
    return 'miles';
  }

  return undefined;
};

const TooltipRow = ({
  label,
  value,
  isTotal,
}: {
  label: string;
  value: string;
  isTotal?: boolean;
}) => (
  <Row spaceBetween centerAlign style={{ gap: 24 }}>
    <Typography.Body
      type={isTotal ? 'Body 14 Bold' : 'Body 13'}
      color={'var(--color-tooltip-text)'}
    >
      {label}
    </Typography.Body>
    <Typography.Body
      type={isTotal ? 'Body 14 Bold' : 'Body 13 Bold'}
      color={'var(--color-tooltip-text)'}
    >
      {value}
    </Typography.Body>
  </Row>
);

const BandTable = ({
  reward,
  rewardBands,
  formatTarget,
  formatBandValue,
}: {
  reward: BonusTypes.Rewards.AnyRewardOutput;
  rewardBands: BonusTypes.Rewards.RewardBands;
  formatTarget: (value: number) => string;
  formatBandValue: (value: number) => string;
}) => (
  <Section>
    <TableTitleDiv>
      <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
        {'Total Points'}
      </Typography.Body>
      <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
        {reward.type === 'multiplierBanded' ? 'Pay Per Mile' : 'Reward'}
      </Typography.Body>
    </TableTitleDiv>
    <Rows>
      {rewardBands.bands.map((band) => {
        const threshold = getBandThreshold(band);
        return (
          <BandRow
            key={`${threshold?.operator}-${threshold?.value}-${band.value}`}
            isActive={band.active}
          >
            <Typography.Body
              type={'Body 13 Bold'}
              color={'var(--color-tooltip-text)'}
            >
              {threshold
                ? `${threshold.operator} ${formatTarget(threshold.value)}`
                : ''}
            </Typography.Body>
            <Typography.Body
              type={'Body 13 Bold'}
              color={'var(--color-tooltip-text)'}
            >
              {formatBandValue(band.value)}
            </Typography.Body>
          </BandRow>
        );
      })}
    </Rows>
  </Section>
);

const PointsToPayConversionTooltip = ({
  reward,
}: {
  reward: BonusTypes.Rewards.AnyRewardOutput;
}) => {
  const { bonusOverview } = useContext(BonusOverviewContext);
  const { getFieldLabel } = useGetFieldLabel();
  const { formatField, formatValue } = useValueFormatters();

  const rewardBands = reward.rewardBands;
  const activeBand = useMemo(() => {
    if (!rewardBands || isArnoldBrosRewardBands(rewardBands)) {
      return undefined;
    }
    return rewardBands.bands.find((band) => band.active);
  }, [rewardBands]);

  if (!bonusOverview || !rewardBands || isArnoldBrosRewardBands(rewardBands)) {
    return null;
  }

  const { dataset } = bonusOverview.bonusProgram;
  const threshold = activeBand ? getBandThreshold(activeBand) : undefined;
  const finalPay = getFinalPay({ reward, activeBand });
  const units = getRewardUnits({
    bonusProgram: bonusOverview.bonusProgram,
    rewardName: reward.name,
  });
  const rateUnits = getRateUnits({
    units,
    multiplierField: rewardBands.multiplierField,
  });

  const formatCurrency = (value: number) =>
    formatValue({
      value,
      formatting: { prefix: 'currency', precision: 2, currencyType: 'USD' },
    }).toString();

  const totalPointsLabel = getFieldLabel({
    field: rewardBands.field,
    dataType: dataset,
  });
  const totalPoints = formatField({
    field: rewardBands.field,
    dataset,
    value: rewardBands.fieldValue,
  }).toString();
  const tier = threshold
    ? `${threshold.operator} ${formatField({
        field: rewardBands.field,
        dataset,
        value: threshold.value,
      })}`
    : undefined;

  const formatTarget = (value: number) =>
    formatField({
      field: rewardBands.field,
      dataset,
      value,
    }).toString();

  const formatBandValue = (value: number) =>
    reward.type === 'multiplierBanded'
      ? formatRewardRate({
          rate: value,
          rewardType: 'monetary',
          units: rateUnits,
        })
      : formatCurrency(value);

  const multiplierMath =
    activeBand &&
    reward.type === 'multiplierBanded' &&
    rewardBands.multiplierField &&
    rewardBands.multiplierFieldValue !== undefined
      ? {
          field: rewardBands.multiplierField,
          rate: activeBand.value,
          value: rewardBands.multiplierFieldValue,
        }
      : undefined;

  return (
    <Outer>
      <Section>
        <TooltipRow label={totalPointsLabel} value={totalPoints} />
      </Section>
      <BandTable
        reward={reward}
        rewardBands={rewardBands}
        formatTarget={formatTarget}
        formatBandValue={formatBandValue}
      />
      {multiplierMath && (
        <Section>
          <Rows>
            {tier ? (
              <TooltipRow label={'Conversion Tier'} value={tier} />
            ) : (
              <Typography.Body
                type={'Body 13'}
                color={'var(--color-tooltip-text-muted)'}
              >
                {'No active conversion tier'}
              </Typography.Body>
            )}
            <TooltipRow
              label={'Pay Per Mile'}
              value={formatRewardRate({
                rate: multiplierMath.rate,
                rewardType: 'monetary',
                units: rateUnits,
              })}
            />
            <TooltipRow
              label={getFieldLabel({
                field: multiplierMath.field,
                dataType: dataset,
              })}
              value={formatField({
                field: multiplierMath.field,
                dataset,
                value: multiplierMath.value,
              }).toString()}
            />
            {finalPay !== undefined && (
              <TooltipRow
                label={'Final Pay'}
                value={formatCurrency(finalPay)}
                isTotal
              />
            )}
          </Rows>
        </Section>
      )}
    </Outer>
  );
};

export default PointsToPayConversionTooltip;
